import type { Key } from 'react'

type MessageContent = string | { content?: unknown; key?: Key }
type MessageMethodName = 'error' | 'success' | 'warning' | 'info'

type MessageApiLike = Partial<Record<MessageMethodName, unknown>>

interface MessageDedupOptions {
    windowMs?: number
}

const DEFAULT_WINDOW_MS = 1500

const normalizeMessageKey = (type: string, content: unknown): string => {
    if (content && typeof content === 'object' && 'key' in content) {
        const keyedContent = content as { key?: unknown; content?: unknown }
        if (keyedContent.key !== undefined) {
            return `${type}:${String(keyedContent.key)}`
        }
        return `${type}:${String(keyedContent.content ?? '')}`
    }

    return `${type}:${String(content)}`
}

export const createDedupedMessageApi = <T extends MessageApiLike>(
    messageApi: T,
    options: MessageDedupOptions = {}
): T => {
    const shownAtByKey = new Map<string, number>()
    const windowMs = options.windowMs ?? DEFAULT_WINDOW_MS

    const wrap =
        (type: MessageMethodName) =>
        (content: MessageContent) => {
            const key = normalizeMessageKey(String(type), content)
            const now = Date.now()
            const lastShownAt = shownAtByKey.get(key)
            if (lastShownAt !== undefined && now - lastShownAt < windowMs) {
                return undefined
            }

            shownAtByKey.set(key, now)
            const method = messageApi[type]
            if (typeof method !== 'function') {
                return undefined
            }
            return (method as (content: MessageContent) => unknown)(content)
        }

    return {
        ...messageApi,
        ...(messageApi.error ? { error: wrap('error') } : {}),
        ...(messageApi.success ? { success: wrap('success') } : {}),
        ...(messageApi.warning ? { warning: wrap('warning') } : {}),
        ...(messageApi.info ? { info: wrap('info') } : {}),
    } as T
}
